import React, {
  useEffect,
  useContext,
  useCallback,
  Fragment,
  useState,
} from 'react';
import './Uniform.css';
import CbmContext from '../../Context/Cbm/cbmContext';
import EmployeeInfo from '../EmployeeInfo/EmployeeInfo';
import { useFormSetup } from '../../hooks/useFormSetup';
import ValidationErrors from '../Utils/ValidationErrors';

const Uniform = ({ setPayLoad, setFormError, errMsg }) => {
  const cbmContext = useContext(CbmContext);
  const {
    portalUser,
    getStates,
    stateList,
    stateListRetrieved,
    auth,
    authLoading,
    submittedData,
    errorCode,
  } = cbmContext;

  const [uniformInfo, setUniformInfo] = useState({
    employee: {
      employeeNumber: 0,
      employeeFirst: '',
      employeeLast: '',
      address: {
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
      },
    },
    total: 0,
    quantity: 0,
    size: '',
    sig: null,
    portalUser,
  });

  // Use unified form setup hook
  const { validationErrors, getFieldError } = useFormSetup({
    formName: 'uniform',
    formData: uniformInfo,
    setFormData: setUniformInfo,
    setPayLoad,
    setFormError,
    submittedData,
    errorCode,
  });

  useEffect(() => {
    // Only fetch states if authenticated and not already retrieved
    if (stateListRetrieved === false && auth && !authLoading) {
      getStates();
    }
  }, [getStates, stateListRetrieved, auth, authLoading]);

  const handleAddressInfo = useCallback(
    (e) => {
      const { name, value } = e.target;
      setUniformInfo((prev) => ({
        ...prev,
        employee: {
          ...prev.employee,
          address: { ...prev.employee.address, [name]: value },
        },
      }));
    },
    [setUniformInfo]
  );

  const onHandleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setUniformInfo((prev) => {
        if (name === 'quantity') {
          const numShirts = parseInt(value);
          return { ...prev, quantity: numShirts, total: numShirts * 7 };
        } else if (
          name === 'employeeNumber' ||
          name === 'employeeFirst' ||
          name === 'employeeLast'
        ) {
          return { ...prev, employee: { ...prev.employee, [name]: value } };
        } else {
          return { ...prev, [name]: value };
        }
      });
    },
    [setUniformInfo]
  );
  return (
    <Fragment>
      <div className="heading">
        <h1>Uniform</h1>
      </div>
      {errMsg && (
        <div className="error-message-container">
          <p className="error-message">{errMsg}</p>
          <ValidationErrors errors={validationErrors} />
        </div>
      )}
      <fieldset className="uniformEmployeeInfo">
        <legend>Employee Information</legend>
        <EmployeeInfo
          payload={uniformInfo}
          changeEmpInfo={onHandleChange}
          getFieldError={getFieldError}
          errMsg={errMsg}
        />
        <div className="addressInfo">
          <div
            className={
              errMsg && getFieldError('employee.address.address1')
                ? 'address1 error'
                : 'address1'
            }
          >
            <label htmlFor="address1" className="required">
              Address:
            </label>
            <input
              type="text"
              id="address1"
              name="address1"
              value={uniformInfo.employee.address.address1}
              className="address1"
              onChange={(e) => handleAddressInfo(e)}
            />
            {errMsg && getFieldError('employee.address.address1') && (
              <span className="error-text">
                {getFieldError('employee.address.address1')}
              </span>
            )}
          </div>
          <div className="address2">
            <label htmlFor="address2" className="required">
              Apt #:
            </label>
            <input
              type="text"
              id="address2"
              name="address2"
              value={uniformInfo.employee.address.address2}
              className="address2"
              onChange={(e) => handleAddressInfo(e)}
            />
          </div>
          <div
            className={
              errMsg && getFieldError('employee.address.city')
                ? 'city error'
                : 'city'
            }
          >
            <label htmlFor="city" className="required">
              City:
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={uniformInfo.employee.address.city}
              className="city"
              onChange={(e) => handleAddressInfo(e)}
            />
            {errMsg && getFieldError('employee.address.city') && (
              <span className="error-text">
                {getFieldError('employee.address.city')}
              </span>
            )}
          </div>
          <div
            className={
              errMsg && getFieldError('employee.address.state')
                ? 'state error'
                : 'state'
            }
          >
            <label htmlFor="state" className="required">
              State:
            </label>
            <select
              id="state"
              name="state"
              className="state"
              value={uniformInfo.employee.address.state}
              onChange={(e) => handleAddressInfo(e)}
            >
              <option>Select state</option>
              {stateList?.map((state, index) => {
                return (
                  <option value={state.stateShortName} key={index}>
                    {state.stateName}
                  </option>
                );
              })}
            </select>
            {errMsg && getFieldError('employee.address.state') && (
              <span className="error-text">
                {getFieldError('employee.address.state')}
              </span>
            )}
          </div>
          <div
            className={
              errMsg && getFieldError('employee.address.zip')
                ? 'zip error'
                : 'zip'
            }
          >
            <label htmlFor="zip" className="required">
              Zip:
            </label>
            <input
              type="text"
              id="zip"
              name="zip"
              value={uniformInfo.employee.address.zip}
              className="zip"
              onChange={(e) => handleAddressInfo(e)}
            />
            {errMsg && getFieldError('employee.address.zip') && (
              <span className="error-text">
                {getFieldError('employee.address.zip')}
              </span>
            )}
          </div>
        </div>
      </fieldset>
      <fieldset>
        <legend>Uniform Info</legend>
        <div className="uniformInfo">
          <div
            className={
              errMsg && getFieldError('quantity')
                ? 'shirtQuantity error'
                : 'shirtQuantity'
            }
          >
            <label htmlFor="quantity">Enter shirt quantity:</label>
            <input
              type="number"
              name="quantity"
              value={uniformInfo.quantity}
              id="quantity"
              onChange={(e) => onHandleChange(e)}
            />
            {errMsg && getFieldError('quantity') && (
              <span className="error-text">{getFieldError('quantity')}</span>
            )}
          </div>
          <div
            className={
              errMsg && getFieldError('size')
                ? 'uniformSize error'
                : 'uniformSize'
            }
          >
            <label>Uniform size:</label>
            <div className="sizes">
              <div className="sizeSmall">
                <label htmlFor="small">S</label>
                <input
                  type="radio"
                  checked={uniformInfo.size === 'Small'}
                  name="size"
                  id="small"
                  value="Small"
                  onChange={(e) => onHandleChange(e)}
                />
              </div>
              <div className="sizeMedium">
                <label htmlFor="medium">M</label>
                <input
                  type="radio"
                  checked={uniformInfo.size === 'Medium'}
                  name="size"
                  id="medium"
                  value="Medium"
                  onChange={(e) => onHandleChange(e)}
                />
              </div>
              <div className="sizeLarge">
                <label htmlFor="large">L</label>
                <input
                  type="radio"
                  checked={uniformInfo.size === 'Large'}
                  name="size"
                  id="large"
                  value="Large"
                  onChange={(e) => onHandleChange(e)}
                />
              </div>
              <div className="sizeXLarge">
                <label htmlFor="x-large">XL</label>
                <input
                  type="radio"
                  checked={uniformInfo.size === 'XLarge'}
                  name="size"
                  id="x-large"
                  value="XLarge"
                  onChange={(e) => onHandleChange(e)}
                />
              </div>
            </div>
            {errMsg && getFieldError('size') && (
              <span className="error-text">{getFieldError('size')}</span>
            )}
          </div>
        </div>
      </fieldset>
    </Fragment>
  );
};
// eslint-disable-next-line
Uniform.propTypes = {};

export default Uniform;
